<template>
  <div class="event-booking-modal">
    <CustomModal :show="show"
                 @close="onClose"
                 static-backdrop>
      <template #header>
        <h1 class="custom-modal__heading">
          <template v-if="edit">
            Edit
          </template>

          <template v-if="wishlistId">
            Wish list booking
          </template>
          <template v-else>
            Booking
          </template>
        </h1>
      </template>

      <div class="event-booking-modal__body">
        <div v-if="error">
          <h3>Error</h3>

          <p>
            Something has gone wrong whilst trying to process your request. Please try again later.
          </p>

          <p>
            If this issue persists, please contact us.
          </p>
        </div>

        <div v-else-if="loading">
          <LoadingSpinner></LoadingSpinner>
        </div>

        <div v-else-if="fullyBooked">
          <h3>Event booking error</h3>
          <p>This event is fully booked.</p>
        </div>

        <div v-else-if="bookingApiErrorCode === 108">
          <h3>Event booking error</h3>
          <p>You have reached the maximum number of delegates permitted from one organisation</p>
          <p>
            <span class="event-booking-modal__asterisk">*</span>
            <i>This will include delegates from other bookings</i>
          </p>
        </div>

        <div v-else-if="bookingApiErrorCode === 120">
          <h3>Event booking error</h3>
          <p>There are not enough spaces availble to complete your booking.</p>
        </div>

        <div v-else-if="instance && webAccountId">
          <template v-if="timedOut">
            <h2 class="event-booking-modal__subheading">
              Your booking has expired
            </h2>

            <p>
              If you still wish to book {{ eventTitle }}, you will need to restart the booking
              process.
            </p>
          </template>

          <template v-else>
            <template v-if="step < 2">
              <h2 class="event-booking-modal__subheading">
                You are booking {{ eventTitle }}
              </h2>

              <div class="event-booking-modal__details">
                <div>
                  <span class="event-booking-modal__item-heading">
                    Date:
                  </span>

                  {{ instance.startDate }}
                </div>
                <div>
                  <span class="event-booking-modal__item-heading">
                    Location:
                  </span>

                  {{ instance.location }}
                </div>
                <div v-if="showPrice">
                  <span class="event-booking-modal__item-heading">
                    Price:
                  </span>

                  {{ instance.priceExVat }}
                </div>
              </div>
            </template>

            <template v-if="step === 0">
              <div class="event-booking-modal__num-delegates">
                <b-form-group label="Select number of delegates">
                  <b-select v-model="selectedQuantity"
                            :options="quantityOptions"></b-select>

                  <span v-if="!isValid && submitAttempted"
                        class="validation-error">
                    Please select a number
                  </span>
                </b-form-group>
              </div>
            </template>

            <template v-if="step === 1">
              <ul class="event-booking-modal__delegates-list">
                <li v-for="i in selectedQuantity"
                    :key="i">
                  <EventBookingDelegate
                    v-model="delegates[i - 1]"
                    :index="i"
                    :show-validity="submitAttempted"
                    :show-dietary-requirements="instance.showDietaryRequirements"
                    :show-disability-options="instance.showDisabilityOptions"
                    :disabilities="disabilities"
                    :dietary-requirements="dietaryRequirements"
                    :job-types="jobTypes"
                    :valid="delegateValidity[i - 1] || inWishlist"
                    :allow-removal="inWishlist"
                    :edit="edit"
                    :orgs="orgs"
                    :delegate-types="delegateTypes"
                    @remove="onDelegateRemove(i - 1)">
                  </EventBookingDelegate>
                </li>
              </ul>

              <div v-if="!isValid && submitAttempted && !inWishlist"
                   class="validation-error">
                Delegate details will be required, in order to checkout this product
                <!-- , or tick the box to skip adding the details until later -->
              </div>
            </template>
            <template v-if="step === 2">
              <template v-if="remainingTime || isWishlistBooking || inWishlist">
                <h2 class="event-booking-modal__subheading">
                  <template v-if="edit">
                    Your booking has been updated
                  </template>
                  <template v-else>
                    <template v-if="isWishlistBooking">
                      Your booking has been added to the wish list
                    </template>
                    <template v-else>
                      Your booking has been added to the basket
                    </template>
                  </template>
                </h2>

                <p v-if="!isWishlistBooking && !inWishlist">
                  Please complete your purchase within
                  {{ remainingTime }}

                  <template v-if="remainingTime === 1">
                    minute
                  </template>
                  <template v-else>
                    minutes
                  </template>

                  to secure your place.
                </p>
              </template>
            </template>
          </template>
        </div>

        <div v-else>
          <p>Please log in to continue.</p>
          <a class="btn btn-accent-6 btn-rounded"
             href="/login">
            Log in
          </a>
        </div>
      </div>

      <template #footer>
        <div v-if="processing"
             class="event-booking-modal__processing">
          <loading-spinner></loading-spinner>
        </div>
        <template v-else-if="!error && webAccountId">
          <button
            v-if="timedOut"
            aria-label="Restart booking"
            class="btn btn-accent-6 btn-rounded"
            @click="onRestart">
            Start again
          </button>

          <button
            v-else-if="fullyBooked"
            class="btn btn-accent-6 btn-rounded"
            @click="onJoinWaitingList">
            Join waiting list
          </button>

          <button v-else-if="bookingApiErrorCode"
                  class="btn btn-accent-6 btn-rounded ml-auto"
                  @click="onClose">
            Close
          </button>

          <button
            v-else-if="step < 2"
            class="btn btn-accent-6 btn-rounded"
            :aria-label="edit && !startedAgain ? 'Save changes' : 'Continue booking'"
            @click="onContinue">
            <template v-if="edit && !startedAgain">
              Save changes
            </template>
            <template v-else>
              Continue booking
            </template>
          </button>

          <button v-else-if="step === 2"
                  class="btn btn-accent-6 btn-rounded"
                  @click="onContinue">
            <template v-if="edit">
              <template v-if="inWishlist">
                Back to wish list
              </template>
              <template v-else>
                Back to basket
              </template>
            </template>
            <template v-else>
              <template v-if="isWishlistBooking">
                Go to wish list
              </template>
              <template v-else>
                Go to basket
              </template>
            </template>
          </button>
        </template>
      </template>
    </CustomModal>
  </div>
</template>

<script>
import { BasketResource } from "spiritweb.packages.ecommerce";
import { addToWishlist } from "~/Scripts/resources/wishlistResource";
import CustomModal from "~/Components/CustomModal.vue";
import EventBookingDelegate from "~/Components/Events/EventBookingDelegate.vue";
import LoadingSpinner from "~/Components/LoadingSpinner.vue";
import { calcBookingAvailability, calcWishlistAvailability } from "~/Scripts/modules/events";
import {
  startBooking,
  updateBooking,
  saveDelegates,
  saveDelegateMetadata,
  abandonBooking,
  listDietaryRequirements,
  listDisabilities,
  listEventOrAllJobTypes,
  loadBooking,
  listDelegateTypesMetadata
} from "~/Scripts/resources/eventsResource";
import { AddressSearchResource } from "spiritweb.packages.web-account";

// #region types
/**
 * @typedef {Object} Address
 * @property {string} addressId UUID
 * @property {string} orgId UUID
 * @property {string} orgName
 * @property {string} line1
 * @property {string} line2
 * @property {string} line3
 * @property {string} town
 * @property {string} region
 * @property {string} postcode
 * @property {number} [countryId]
 * @property {string} country
 */

/**
 * @typedef {Object} Session
 * @property {string} name
 * @property {string} description
 * @property {string} endDateTime Date is already a formatted string
 * @property {string} startDateTime Date is already a formatted string
 * @property {Address} address
 * @property {boolean} limitNumberOfSpaces
 * @property {boolean} [mandatory]
 * @property {number} [maxSpaces]
 * @property {number} [spacesAvailable]
 * @property {number} [spacesBooked]
 */

/**
 * @typedef {Object} EventInstance
 * @property {string} id UUID
 * @property {string} eventId UUID
 * @property {string} [defaultSessionId] UUID
 * @property {number} [productId]
 * @property {boolean} showLocation
 * @property {boolean} showDisabilityOptions
 * @property {boolean} showDietaryRequirements
 * @property {string} location
 * @property {string} duration
 * @property {string} startDate Date is already a formatted string
 * @property {string} endDate Date is already a formatted string
 * @property {string} address
 * @property {string} postcode
 * @property {string} priceExVat
 * @property {number} [vatRate] double
 * @property {boolean} applyVat
 * @property {number} productId
 * @property {number} [spacesAvailable]
 * @property {number} [minDelegates]
 * @property {number} [maxDelegates]
 * @property {number} [maxDelegatesPerOrganisation]
 * @property {number} [maxDelegatesOnWaitingList]
 * @property {Array<Session>} sessions
 * @property {string} instanceType
 */
// #endregion types

export default {
  name: "EventBookingModal",
  components: {
    CustomModal,
    EventBookingDelegate,
    LoadingSpinner
  },
  props: {
    value: {
      type: String,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    },
    /** @type {import('vue').PropType<EventInstance>} */
    instance: {
      type: Object,
      default: () => ( {} )
    },
    eventTitle: {
      type: String,
      default: null
    },
    webAccountId: {
      type: String,
      default: null
    },
    pricingScheduleId: {
      type: Number,
      default: null
    },
    edit: {
      type: Boolean,
      default: false
    },
    wishlistId: {
      type: String,
      default: null
    },
    inWishlist: {
      type: Boolean,
      default: false
    },
    bookingStatus: {
      type: String,
      default: null
    },
    orgId: {
      type: String,
      default: null
    },
    bookingFromWishlist: {
      type: Boolean,
      default: false
    },
    relationshipTypeIds: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      step: 0,
      selectedQuantity: null,
      delegates: [],
      submitAttempted: false,
      bookingId: null,
      orderLineId: null,
      holdExpiryDateTime: null,
      remainingTime: null,
      interval: null,
      disabilities: [],
      dietaryRequirements: [],
      jobTypes: [],
      orgs: [],
      error: false,
      loading: false,
      startedAgain: false,
      fullyBooked: false,
      bookingApiErrorCode: null,
      showDietaryRequirements: undefined,
      showDisabilityOptions: undefined,
      processing: false,
      delegateTypes: []
    };
  },
  computed: {
    delegateValidity () {
      const result = [];

      const mandatory = ["firstName", "lastName", "emailAddress"];

      this.delegates.forEach( delegate => {
        let delegateValid = false;
        if ( delegate.addDetailsLater ) {
          delegateValid = true;
        } else {
          // eslint-disable-next-line
          const mandatoryFields = Object.entries(delegate).filter(([key]) => mandatory.find( y => y === key )
          );

          delegateValid = mandatoryFields.every( ( [, value] ) => value );

          if ( !this.emailIsValid( delegate.emailAddress ) ) {
            delegateValid = false;
          }
        }

        result.push( delegateValid );
      } );

      return result;
    },
    quantityOptions () {
      const result = [];
      if ( this.instance !== null ) {
        const max = this.wishlistId === null
          ? calcBookingAvailability( this.instance )
          : calcWishlistAvailability( this.instance );

        for ( let i = 1; i <= max; i += 1 ) {
          result.push( {
            value: i,
            text: i
          } );
        }
      }

      return result;
    },
    isValid () {
      let result = false;

      if ( this.step === 0 ) {
        if ( this.selectedQuantity ) {
          result = true;
        }
      } else if ( this.step === 1 ) {
        result = this.delegateValidity.every( x => x );
      } else {
        result = true;
      }

      return result;
    },
    isWishlistBooking () {
      return this.wishlistId !== null && this.wishlistId !== undefined;
    },
    timedOut () {
      let result = false;

      if ( this.remainingTime !== null && !this.inWishlist ) {
        result = this.remainingTime <= 0;
      }

      return result;
    },
    showPrice () {
      let show = true;

      if ( this.bookingFromWishlist && this.instance && this.instance.priceExVat && this.instance.priceExVat === "£0.00" ) {
        show = false;
      }

      return show;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler ( val ) {
        this.bookingId = val;
      }
    },
    instance: {
      deep: true,
      handler ( val ) {
        if ( val ) {
          const { id, showDietaryRequirements, showDisabilityOptions } = val;

          this.showDietaryRequirements = showDietaryRequirements;
          this.showDisabilityOptions = showDisabilityOptions;

          if ( showDietaryRequirements ) {
            this.setDietaryRequirementsList();
          }
          if ( showDisabilityOptions ) {
            this.setDisabilitiesList();
          }
          this.setDelegateTypesList( id );
          this.setJobTypes( id );
        } else {
          this.disabilities = [];
          this.dietaryRequirements = [];
          this.jobTypes = [];
        }
      }
    },
    bookingId: {
      immediate: true,
      handler ( val ) {
        this.$emit( "input", val );

        if ( val && this.edit ) {
          this.loading = true;

          loadBooking( val ).then( response => {
            if ( response.data ) {
              const delegates = response.data.delegates.map( x => this.mapDelegate( x ) );

              if ( delegates?.length > 0 ) {
                this.delegates = delegates;
              } else if ( this.startedAgain ) {
                this.delegates = this.delegates.map( x => {
                  const { id, ...rest } = x;

                  return {
                    id: null,
                    ...rest
                  };
                } );
              }

              this.orderLineId = response.data.orderLineId;
              this.holdExpiryDateTime = response.data.holdExpiryDateTime;
              this.selectedQuantity = response.data.numberOfDelegates;

              this.step = 1;

              this.loading = false;
            }
          } );
        }
      }
    },
    step () {
      this.submitAttempted = false;
    },
    selectedQuantity ( val ) {
      const difference = val - this.delegates.length;
      if ( difference > 0 ) {
        for ( let i = 0; i < difference; i += 1 ) {
          this.delegates.push( {
            id: null,
            title: null,
            firstName: null,
            lastName: null,
            jobTitle: null,
            jobTypeId: null,
            emailAddress: null,
            notes: null,
            dietaryRequirementIds: [],
            disabilityIds: [],
            addDetailsLater: false,
            eventDelegateTypeId: null,
            metaData: null
          } );
        }
      } else if ( difference < 0 ) {
        for ( let i = difference * -1; i > 0; i -= 1 ) {
          this.delegates.pop();
        }
      }
    },
    holdExpiryDateTime ( val ) {
      const clearInterval = () => {
        window.clearInterval( this.interval );
        this.interval = null;
      };

      const setRemainingTime = () => {
        const time = this.getRemainingTime();

        if ( time > 0 ) {
          this.remainingTime = time;
        } else {
          this.remainingTime = 0;
          clearInterval();
        }
      };
      setRemainingTime();
      if ( val ) {
        if ( !this.interval ) {
          this.interval = window.setInterval( setRemainingTime, 1000 );
        }
      } else {
        clearInterval();
      }
    },
    timedOut ( val ) {
      if ( val && !this.edit ) {
        BasketResource.removeItem( this.orderLineId );
      }
    }
  },
  mounted () {
    this.getOrgs();
  },
  errorCaptured () {
    this.error = true;
  },
  methods: {
    async fetch ( apiPromise, ...args ) {
      return new Promise( ( resolve, reject ) => {
        apiPromise( ...args )
          .then( response => {
            const { status, data } = response;
            if ( status === 200 && data ) {
              resolve( data );
            } else {
              throw new Error( "Bad response." );
            }
          } )
          .catch( err => {
            reject( err );
          } );
      } );
    },
    async setJobTypes ( id ) {
      return this.fetch( listEventOrAllJobTypes, id )
        .then( response => {
          this.jobTypes = response;
        } )
        .catch( console.error );
    },
    async setDietaryRequirementsList () {
      return this.fetch( listDietaryRequirements )
        .then( response => {
          this.dietaryRequirements = response;
        } )
        .catch( console.error );
    },
    async setDisabilitiesList () {
      return this.fetch( listDisabilities )
        .then( response => {
          this.disabilities = response;
        } )
        .catch( console.error );
    },
    async setDelegateTypesList ( id ) {
      return this.fetch( listDelegateTypesMetadata, id )
        .then( response => {
          this.delegateTypes = response;
        } )
        .catch( console.error );
    },
    mapDelegate ( delegate ) {
      const {
        eventDelegateId: id,
        dietaryRequirements,
        disabilities,
        unallocated: addDetailsLater,
        ...rest
      } = delegate;

      const dietaryRequirementIds = dietaryRequirements?.map( x => x.eventDietaryRequirementId );

      const disabilityIds = disabilities?.map( x => x.eventDisabilityId );

      return {
        id,
        dietaryRequirementIds,
        disabilityIds,
        addDetailsLater,
        ...rest
      };
    },
    getRemainingTime () {
      let result = 10; // minutes

      if ( this.holdExpiryDateTime ) {
        const date = new Date( this.holdExpiryDateTime );
        result = Math.floor( ( date - Date.now() ) / 1000 / 60 );
      }

      if ( result < 0 ) {
        result = 0;
      }

      return result;
    },
    addProduct () {
      return new Promise( ( resolve, reject ) => {
        if ( !this.orderLineId ) {
          const { productId } = this.instance;

          if ( this.wishlistId ) {
            // Wishlist
            addToWishlist(
              this.wishlistId,
              productId,
              this.pricingScheduleId,
              0,
              0,
              this.selectedQuantity
            )
              .then( response => {
                this.orderLineId = response.data;
                resolve();
              } )
              .catch( e => {
                reject( e );
              } );
          } else {
            // Bookings
            const payload = {
              id: this.instance.productId,
              pricingScheduleId: this.pricingScheduleId,
              quantity: this.selectedQuantity
            };

            this.$root.$emit( "sw::addToBasket::request", payload );
            BasketResource.addProduct( productId, this.selectedQuantity, {
              pricingScheduleId: this.pricingScheduleId,
              productFormatId: 0,
              productLicenceTypeId: 0
            } )
              .then( ( { data } ) => {
                let errorCode;
                let errorMessage;

                if ( data && data.length ) {
                  let orderLineId;
                  try {
                    const [first] = data;
                    const { orderLineResult } = first;
                    const {
                      errorCode: errorCodeResult,
                      errorMessage: errorMessageResult,
                      orderLineId: orderLinedIdResult
                    } = orderLineResult;

                    errorCode = errorCodeResult;
                    errorMessage = errorMessageResult;
                    orderLineId = orderLinedIdResult;
                  } catch ( ex ) {
                    throw new Error(
                      "Potential schema issue with product response. Check data: ",
                      data
                    );
                  }

                  if ( orderLineId && errorCode === 100 ) {
                    this.orderLineId = orderLineId;

                    const successPayload = {
                      ...payload,
                      orderLineIds: data
                    };

                    this.$root.$emit( "sw::addToBasket::success", successPayload );

                    resolve( errorMessage );
                  } else {
                    const exceptionMessage = `Server response indicates a failure. Error Message: ${errorMessage}, code: ${errorCode}`;
                    console.error( exceptionMessage, "Check data: ", data );
                    throw new Error( exceptionMessage );
                  }
                } else {
                  throw new Error(
                    `Server response indicates a failure. Error message: ${errorMessage}`
                  );
                }
              } )
              .catch( e => {
                reject( e );
                this.$root.$emit( "sw::addToBasket::failure", e );
              } )
              .finally( () => {
                this.$root.$emit( "sw::addToBasket::response", payload );
              } );
          }
        } else {
          const payload = {
            orderLineId: this.orderLineId,
            quantity: this.selectedQuantity,
            pricingScheduleId: this.pricingScheduleId,
            productFormatId: 0,
            productLicenceTypeId: 0
          };

          this.$root.$emit( "sw::setBasketItemQuantity::start" );
          this.$root.$emit( "sw::setBasketItemQuantity::update", payload );
          BasketResource.setQuantity( this.orderLineId, this.selectedQuantity )
            .then( () => {
              resolve();
            } )
            .catch( e => {
              reject( e );
            } )
            .finally( () => {
              this.$root.$emit( "sw::setBasketItemQuantity::stop" );
            } );
        }
      } );
    },
    removeItem () {
      return new Promise( ( resolve, reject ) => {
        const payload = { orderLineId: this.orderLineId };
        this.$root.$emit( "sw::removeFromBasket::request", payload );
        BasketResource.removeItem( this.orderLineId )
          .then( response => {
            this.$root.$emit( "sw::removeFromBasket::success", response );
            resolve();
          } )
          .catch( error => {
            this.$root.$emit( "sw::removeFromBasket::failure", error );
            reject( error );
          } )
          .finally( () => {
            this.$root.$emit( "sw::removeFromBasket::response", payload );
          } );
      } );
    },
    emailIsValid ( email ) {
      return /\S+@\S+\.\S+/.test( email );
    },
    startBooking () {
      return new Promise( ( resolve, reject ) => {
        startBooking(
          this.instance.id,
          this.orderLineId,
          this.selectedQuantity,
          this.isWishlistBooking,
          null
        )
          .then( response => {
            if ( response.data ) {
              const { apiErrorCode, fullyBooked, result } = response.data;
              if ( apiErrorCode ) {
                this.removeItem( this.orderLineId )
                  .then( () => resolve() )
                  .catch( e => reject( e ) );

                if ( fullyBooked ) {
                  this.fullyBooked = true;
                } else {
                  this.bookingApiErrorCode = apiErrorCode;
                }
              } else if ( result ) {
                this.bookingId = response.data.result.eventBookingId;
                this.holdExpiryDateTime = response.data.result.holdExpiryDateTime;
                resolve();
              } else {
                reject( new Error( "Invalid server response" ) );
              }
            }
          } )
          .catch( e => {
            reject( e );
          } );
      } );
    },
    async saveDelegates () {
      const delegates = this.delegates.map( x => ( {
        ...x,
        sendConfirmationEmails: false
      } ) );

      const response = await saveDelegates( this.bookingId, delegates );

      // Save the delegates meta data
      if ( response?.data?.ids && response.data.ids.length > 0 ) {
        const metaDatas = {};
        for ( let i = 0; i < response.data.ids.length; i += 1 ) {
          const delegateId = response.data.ids[i];
          const { metaData } = this.delegates[i];

          if ( delegateId && metaData ) {
            metaDatas[delegateId] = metaData;
          }
        }
        if ( metaDatas ) {
          await saveDelegateMetadata( metaDatas );
        }
      }
    },
    updateBooking () {
      let delegateCount = 0;
      if ( this.delegates ) {
        delegateCount = this.delegates.length;
      }
      return new Promise( ( resolve, reject ) => {
        updateBooking(
          this.instance.id,
          this.value,
          this.webAccountId,
          this.bookingStatus,
          this.orderLineId,
          this.orgId,
          delegateCount
        )
          .then( response => {
            if ( response.data ) {
              resolve();
            }
          } )
          .catch( e => {
            reject( e );
          } );
      } );
    },
    onDelegateRemove ( index ) {
      this.selectedQuantity -= 1;
      this.delegates.splice( index, 1 );
    },
    onContinue () {
      this.submitAttempted = true;

      if ( !this.processing ) {
        if ( this.step === 0 ) {
          if ( this.isValid ) {
            this.processing = true;
            this.addProduct()
              .then( () => this.startBooking() )
              .then( () => {
                this.step += 1;
              } )
              .catch( e => {
                this.error = true;
                throw e;
              } )
              .finally( () => {
                this.processing = false;
              } );
          }
        } else if ( this.step === 1 ) {
          if ( this.isValid || this.inWishlist ) {
            this.processing = true;
            this.saveDelegates()
              .then( () => {
                if ( this.inWishlist ) {
                  return this.updateBooking();
                }
                return Promise.resolve();
              } )
              .then( () => {
                this.step += 1;
              } )
              .catch( e => {
                this.error = true;
                throw e;
              } )
              .finally( () => {
                this.processing = false;
              } );
          }
        } else if ( this.step === 2 ) {
          if ( this.edit ) {
            this.onClose();
          } else if ( this.isWishlistBooking ) {
            window.location = `/wishlists/${this.wishlistId}`;
          } else {
            window.location = "/basket";
          }
        }
      }
    },
    onRestart () {
      this.bookingId = null;
      this.holdExpiryDateTime = null;

      if ( !this.edit ) {
        this.orderLineId = null;
      }

      this.step = 0;
      this.startedAgain = true;
    },
    onJoinWaitingList () {
      const { id } = this.instance;

      this.$emit( "close" );
      this.resetState();
      this.$emit( "join", id, this.selectedQuantity );
    },
    onClose () {
      if ( !this.processing ) {
        this.processing = true;
        if ( this.bookingId && !this.edit && this.step < 2 ) {
          abandonBooking( this.bookingId )
            .then( () => this.removeItem( this.orderLineId ) )
            .then( () => {
              this.$emit( "close" );
              this.resetState();
            } )
            .catch( e => {
              this.error = true;
              throw e;
            } )
            .finally( () => {
              this.processing = false;
            } );
        } else {
          this.$emit( "close" );
          this.resetState();
        }
      }
    },

    /**
     * Get a list of organisations the user can select from
     */
    getOrgs () {
      const relTypeStr = this.relationshipTypeIds?.join();
      const includeRelationships = this.relationshipTypeIds?.length > 0;
      AddressSearchResource.listFamily( null, relTypeStr, true, includeRelationships )
        .then( response => {
          this.orgs = response?.data || [];
        } )
        .catch( err => {
          console.error( err );
        } );
    },

    resetState () {
      this.bookingId = null;
      this.orderLineId = null;
      this.holdExpiryDateTime = null;
      this.startedAgain = false;
      this.step = 0;
      this.selectedQuantity = null;
      this.delegates = [];
      this.processing = false;
      this.error = false;
    }
  }
};
</script>

<style lang="less" scoped>
.event-booking-modal {
  &__processing {
    font-size: 0.5rem;
  }

  &__asterisk {
    content: "/002A";
  }

  &__subheading {
    font-size: 16px;
    color: #000;
    padding: 15px 0;
  }

  &__details {
    margin-bottom: 15px;
  }

  &__item-heading {
    font-weight: 600;
  }

  &__num-delegates {
    max-width: 200px;

    .form-group {
      margin-bottom: 0;
    }
  }

  &__delegates-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__add-another {
    color: #0c499c;
    padding: 0;
    border: 0;
    background: none;
    font-size: 14px;

    &:hover {
      color: darken(#0c499c, 15%);
      text-decoration: underline;
    }
  }
}
</style>
